// Patient service requests mini-list, used in PatientDetailPage.

import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import type { JSX } from "react";
import { apiFetch } from "../lib/api.js";
import { formatCarerRef } from "../lib/refs.js";
import { SrStatusBadge } from "./SrStatusBadge.js";

interface SrItem {
  _id: string; service_type: string; status: string;
  assigned_carer_id: string | null; created_at: string;
}
interface SrListResponse { data: SrItem[]; total: number; page: number; limit: number; }

export function SrMiniList({ patientId }: { patientId: string }): JSX.Element {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<SrListResponse>({
    queryKey: ["patient-srs", patientId],
    queryFn: () => apiFetch<SrListResponse>(`/sr?patient_id=${patientId}&limit=10`),
  });
  const items = data?.data ?? [];
  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 mb-4">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Service Requests</p>
      {isLoading && <p className="text-sm text-slate-400">Loading…</p>}
      {!isLoading && items.length === 0 && <p className="text-sm text-slate-400">No service requests.</p>}
      {items.length > 0 && (
        <table className="w-full text-sm text-left">
          <thead>
            <tr className="border-b border-slate-100 text-xs text-slate-500 uppercase tracking-wide">
              <th className="py-2 pr-4 font-medium">Date</th>
              <th className="py-2 pr-4 font-medium">Type</th>
              <th className="py-2 pr-4 font-medium">Status</th>
              <th className="py-2 font-medium">Carer</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-50">
            {items.map((sr) => (
              <tr key={sr._id} onClick={() => navigate(`/sr/${sr._id}`)}
                className="hover:bg-navy-50 cursor-pointer transition-colors">
                <td className="py-2 pr-4 text-slate-500 whitespace-nowrap">
                  {new Date(sr.created_at).toLocaleDateString("en-IN", { dateStyle: "short" })}
                </td>
                <td className="py-2 pr-4 text-slate-700 capitalize">{sr.service_type.replace(/_/g, " ")}</td>
                <td className="py-2 pr-4"><SrStatusBadge status={sr.status} /></td>
                <td className="py-2 font-mono text-xs text-slate-500">
                  {sr.assigned_carer_id ? formatCarerRef({ _id: sr.assigned_carer_id }) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
